"use client"

import React from "react"
import { openModal } from "@locmod/modal"
import { useChain } from "@azuro-org/sdk"
import { useWallet } from "wallet"

import { Button, buttonMessages, type ButtonProps } from "components/inputs"
import { sendGTMEvent } from "@next/third-parties/google"
import { useWalletAuth } from "src/hooks/useWalletAuth"
import useChainProviderHook from "@/hooks/useChainProvider"
import { useUserContext } from "src/contexts/user"

type ConnectButtonWrapperProps = {
  children: React.ReactElement<ButtonProps>
}

const ConnectButtonWrapper: React.FC<ConnectButtonWrapperProps> = ({
  children,
}) => {
  const { appChain } = useChain()
  const { account, chainId, isAAWallet, authenticated } = useWallet()
  const { isUserConnecting, currentUser, isLoginComplete } = useUserContext()
  const { isReconnecting, isConnecting, connectOrCreateWallet } =
    useWalletAuth()

  const { isOvertimeChain, isSXChain } = useChainProviderHook()

  const { onClick, title, disabled, ...props } = children.props

  // Use EXACTLY the same logic as RightSidebar to avoid infinite loading issues
  // First check: no account at all
  if (!account) {
    const handleClick = () => {
      connectOrCreateWallet()
      sendGTMEvent({
        event: "wallet_connect_initiated",
        is_betslip: true,
        section: "betslip",
        login_toolkit: "privy",
      })
    }

    return (
      <Button
        {...props}
        title={buttonMessages.connectWallet}
        size={props?.size || 40}
        loading={isConnecting || isReconnecting || isUserConnecting}
        className={props?.className || "w-full"}
        onClick={handleClick}
      />
    )
  }

  // Second check: account exists but user is not fully logged in (same as RightSidebar)
  if (!Boolean(isLoginComplete && !isConnecting)) {
    const handleClick = () => {
      connectOrCreateWallet()
      sendGTMEvent({
        event: "wallet_connect_initiated",
        is_betslip: true,
        section: "betslip",
        login_toolkit: "privy",
      })
    }

    return (
      <Button
        {...props}
        title={buttonMessages.connectWallet}
        size={props?.size || 40}
        loading={isConnecting || isReconnecting || isUserConnecting}
        className={props?.className || "w-full"}
        onClick={handleClick}
      />
    )
  }

  const isRightNetwork = appChain.id === chainId || isOvertimeChain || isSXChain

  if (!isRightNetwork && !isAAWallet) {
    const handleClick = () => {
      openModal("SwitchNetworkModal", {
        chainId: appChain.id,
      })
    }

    return (
      <Button
        {...props}
        title={buttonMessages.changeNetwork}
        size={props?.size || 40}
        className={props?.className || "w-full"}
        onClick={handleClick}
        leftIcon={undefined}
      />
    )
  }

  return children
}

export default ConnectButtonWrapper
